import React, { useState } from 'react';
import { getApiUrl } from '../../config/api'

// YouTube 비디오 ID 추출 함수
const extractVideoId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// YouTube oEmbed API를 사용하여 비디오 정보 가져오기
const getYouTubeEmbed = async (videoId) => {
  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('YouTube oEmbed API 호출 실패:', error);
  }
  return null;
};

const YoutubeVideosApi = () => {
  const [formData, setFormData] = useState({
    part: 'snippet,contentDetails,statistics',
    chart: 'mostPopular',
    regionCode: 'KR',
    videoEmbeddable: 'true'
    // videoCategoryId 제거 - 기본적으로 모든 카테고리의 인기 동영상
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [videoPreviews, setVideoPreviews] = useState({});
  const [multiselectOpen, setMultiselectOpen] = useState({});
  const [copyStatus, setCopyStatus] = useState({});
  const [constraintAlerts, setConstraintAlerts] = useState({});
  const [selectedVideos, setSelectedVideos] = useState(new Set());

  const parameterConfigs = [
    {
      name: 'part',
      label: 'Part',
      description: '반환할 리소스 부분을 지정합니다. 여러 부분을 쉼표로 구분하여 요청할 수 있습니다.',
      type: 'select',
      required: true,
      options: [
        { value: 'snippet', label: 'snippet (기본 정보: 제목, 설명, 채널, 썸네일, 업로드 날짜)' },
        { value: 'contentDetails', label: 'contentDetails (상세 정보: 동영상 길이, 차원, 정의)' },
        { value: 'statistics', label: 'statistics (통계: 조회수, 좋아요 수, 댓글 수)' },
        { value: 'status', label: 'status (상태: 업로드 상태, 개인정보 설정, 라이선스)' },
        { value: 'snippet,contentDetails', label: 'snippet,contentDetails (기본 정보 + 상세 정보)' },
        { value: 'snippet,statistics', label: 'snippet,statistics (기본 정보 + 통계)' },
        { value: 'snippet,contentDetails,statistics', label: 'snippet,contentDetails,statistics (모든 정보)' }
      ]
    },
    {
      name: 'chart',
      label: 'Chart',
      description: '인기 동영상 차트를 요청할 때 사용합니다. mostPopular만 지원됩니다.',
      type: 'select',
      required: false,
      options: [
        { value: 'mostPopular', label: 'mostPopular (인기 동영상)' }
      ]
    },
    {
      name: 'regionCode',
      label: 'Region Code',
      description: '지정된 지역의 인기 동영상을 반환합니다. ISO 3166-1 alpha-2 국가 코드를 사용합니다.',
      type: 'select',
      required: false,
      options: [
        { value: 'KR', label: 'KR (한국)' },
        { value: 'US', label: 'US (미국)' },
        { value: 'JP', label: 'JP (일본)' },
        { value: 'GB', label: 'GB (영국)' },
        { value: 'CA', label: 'CA (캐나다)' },
        { value: 'AU', label: 'AU (호주)' },
        { value: 'DE', label: 'DE (독일)' },
        { value: 'FR', label: 'FR (프랑스)' },
        { value: 'IN', label: 'IN (인도)' },
        { value: 'BR', label: 'BR (브라질)' }
      ]
    },
    {
      name: 'videoCategoryId',
      label: 'Video Category ID',
      description: '특정 카테고리의 동영상만 반환합니다. chart=mostPopular 사용 시 일부 카테고리는 제한될 수 있습니다. 여러 카테고리를 선택할 수 있습니다.',
      type: 'checkbox',
      required: false,
             options: [
         { value: '1', label: '1 (영화 및 애니메이션)', supported: true },
         { value: '2', label: '2 (자동차)', supported: true },
         { value: '10', label: '10 (음악)', supported: true },
         { value: '15', label: '15 (애완동물 및 동물)', supported: true },
         { value: '17', label: '17 (스포츠)', supported: true },
         { value: '19', label: '19 (여행 및 이벤트) - mostPopular에서 제한됨', supported: false },
         { value: '20', label: '20 (게임)', supported: true },
         { value: '22', label: '22 (인물 및 블로그)', supported: true },
         { value: '23', label: '23 (코미디)', supported: true },
         { value: '24', label: '24 (엔터테인먼트)', supported: true },
         { value: '25', label: '25 (뉴스 및 정치)', supported: true },
         { value: '26', label: '26 (스타일 및 뷰티)', supported: true },
         { value: '27', label: '27 (교육)', supported: true },
         { value: '28', label: '28 (과학 및 기술)', supported: true },
         { value: '29', label: '29 (비영리 및 활동)', supported: true },
         { value: '30', label: '30 (영화)', supported: true },
         { value: '31', label: '31 (애니메이션) - mostPopular에서 제한됨', supported: false },
         { value: '32', label: '32 (액션/어드벤처)', supported: true },
         { value: '33', label: '33 (클래식)', supported: true },
         { value: '34', label: '34 (다큐멘터리)', supported: false },
         { value: '35', label: '35 (드라마)', supported: false },
         { value: '36', label: '36 (가족)', supported: false },
         { value: '37', label: '37 (외국어)', supported: false },
         { value: '38', label: '38 (공포)', supported: false },
         { value: '39', label: '39 (SF 및 판타지)', supported: false },
         { value: '40', label: '40 (스릴러)', supported: false },
         { value: '41', label: '41 (단편)', supported: false },
         { value: '42', label: '42 (쇼)', supported: false },
         { value: '43', label: '43 (예고편)', supported: false },
         { value: '44', label: '44 (기타)', supported: false }
       ]
    },
    {
      name: 'maxResults',
      label: 'Max Results',
      description: '반환할 최대 결과 수를 지정합니다. 기본값은 5, 최대값은 50입니다.',
      type: 'select',
      required: false,
      options: [
        { value: '5', label: '5개' },
        { value: '10', label: '10개' },
        { value: '15', label: '15개' },
        { value: '20', label: '20개' },
        { value: '25', label: '25개' },
        { value: '30', label: '30개' },
        { value: '40', label: '40개' },
        { value: '50', label: '50개' }
      ]
    },
    {
      name: 'videoDuration',
      label: 'Video Duration',
      description: '동영상 길이에 따라 필터링합니다. short(4분 미만), medium(4-20분), long(20분 이상)',
      type: 'select',
      required: false,
      options: [
        { value: 'short', label: 'short (4분 미만)' },
        { value: 'medium', label: 'medium (4-20분)' },
        { value: 'long', label: 'long (20분 이상)' }
      ]
    },
    {
      name: 'videoDefinition',
      label: 'Video Definition',
      description: '동영상 해상도에 따라 필터링합니다. high(HD), standard(SD)',
      type: 'select',
      required: false,
      options: [
        { value: 'high', label: 'high (HD)' },
        { value: 'standard', label: 'standard (SD)' }
      ]
    },
    {
      name: 'videoCaption',
      label: 'Video Caption',
      description: '자막 유무에 따라 필터링합니다. closedCaption(자막 있음), none(자막 없음)',
      type: 'select',
      required: false,
      options: [
        { value: 'closedCaption', label: 'closedCaption (자막 있음)' },
        { value: 'none', label: 'none (자막 없음)' }
      ]
    },
    {
      name: 'videoLicense',
      label: 'Video License',
      description: '동영상 라이선스 유형에 따라 필터링합니다.',
      type: 'select',
      required: false,
      options: [
        { value: 'creativeCommon', label: 'creativeCommon (크리에이티브 커먼즈)' },
        { value: 'youtube', label: 'youtube (표준 YouTube 라이선스)' }
      ]
    },
    {
      name: 'videoEmbeddable',
      label: 'Video Embeddable',
      description: '외부 사이트 임베드 가능 여부에 따라 필터링합니다.',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'true (임베드 가능)' },
        { value: 'false', label: 'false (임베드 불가)' }
      ]
    },
    {
      name: 'videoSyndicated',
      label: 'Video Syndicated',
      description: '제휴 사이트에서 재생 가능 여부에 따라 필터링합니다.',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'true (제휴 가능)' },
        { value: 'false', label: 'false (제휴 불가)' }
      ]
    },
    {
      name: 'id',
      label: 'Video ID (쉼표로 구분)',
      description: '특정 동영상 ID를 쉼표로 구분하여 여러 개 지정할 수 있습니다. chart 파라미터와 함께 사용할 수 없습니다.',
      type: 'text',
      required: false,
      placeholder: '예: dQw4w9WgXcQ,9bZkp7q19f0'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 다중 선택 체크박스 핸들러
  const handleMultiselectChange = (name, value) => {
    setFormData(prev => {
      const currentValues = prev[name] ? prev[name].split(',').filter(v => v.trim()) : [];
      
      if (currentValues.includes(value)) {
        // 값이 이미 있으면 제거
        const newValues = currentValues.filter(v => v !== value);
        return {
          ...prev,
          [name]: newValues.join(',')
        };
      } else {
        // 값이 없으면 추가
        const newValues = [...currentValues, value];
        return {
          ...prev,
          [name]: newValues.join(',')
        };
      }
    });
  };

  // 파라미터 전체선택/전체해제 핸들러
  const handleParamSelectAll = (name, options) => {
    setFormData(prev => {
      const currentValues = prev[name] ? prev[name].split(',').filter(v => v.trim()) : [];
      const allValues = options.map(option => option.value);
      
      // 현재 모든 값이 선택되어 있으면 전체 해제, 아니면 전체 선택
      const isAllSelected = allValues.every(value => currentValues.includes(value));
      
      if (isAllSelected) {
        return {
          ...prev,
          [name]: ''
        };
      } else {
        return {
          ...prev,
          [name]: allValues.join(',')
        };
      }
    });
  };

  // 다중 선택 드롭다운 토글
  const toggleMultiselect = (name) => {
    setMultiselectOpen(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const params = new URLSearchParams();
      params.append('endpoint', 'videos');
      
      // 빈 값이 아닌 파라미터만 추가
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          params.append(key, value.trim());
        }
      });


      const response = await fetch(`${getApiUrl('/api/youtube/dynamic')}?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setResult(data);
        
        // YouTube 비디오 미리보기 가져오기
        if (data.result && data.result.items && data.result.items.length > 0) {
          const previews = {};
          for (const item of data.result.items) {
            if (item.id && item.id.videoId) {
              const embedData = await getYouTubeEmbed(item.id.videoId);
              if (embedData) {
                previews[item.id.videoId] = embedData;
              }
            } else if (item.id && typeof item.id === 'string') {
              const embedData = await getYouTubeEmbed(item.id);
              if (embedData) {
                previews[item.id] = embedData;
              }
            }
          }
          setVideoPreviews(previews);
        }
      } else {
        console.error('❌ YouTube API 오류 상세:', data);
        setError(data.message || 'API 호출 실패');
      }
    } catch (err) {
      console.error('❌ YouTube API 동적 호출 실패:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 조회 버튼 핸들러 (API 호출만)
  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const params = new URLSearchParams();
      params.append('endpoint', 'videos');
      
      // 빈 값이 아닌 파라미터만 추가
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          params.append(key, value.trim());
        }
      });


      const response = await fetch(`${getApiUrl('/api/youtube/dynamic')}?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setResult(data);
        
        // YouTube 비디오 미리보기 가져오기
        if (data.result && data.result.items && data.result.items.length > 0) {
          const previews = {};
          for (const item of data.result.items) {
            if (item.id && item.id.videoId) {
              const embedData = await getYouTubeEmbed(item.id.videoId);
              if (embedData) {
                previews[item.id.videoId] = embedData;
              }
            } else if (item.id && typeof item.id === 'string') {
              const embedData = await getYouTubeEmbed(item.id);
              if (embedData) {
                previews[item.id] = embedData;
              }
            }
          }
          setVideoPreviews(previews);
        }
      } else {
        console.error('❌ YouTube API 오류 상세:', data);
        setError(data.message || 'API 호출 실패');
      }
    } catch (err) {
      console.error('❌ YouTube API 동적 호출 실패:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 비디오 선택/해제 핸들러
  const handleVideoSelect = (videoId) => {
    setSelectedVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  // 전체 선택/해제 핸들러
  const handleSelectAll = () => {
    if (!result || !result.result || !result.result.items) return;
    
    const allVideoIds = result.result.items.map(item => {
      const videoId = item.id?.videoId || (typeof item.id === 'string' ? item.id : null);
      return videoId;
    }).filter(Boolean);

    setSelectedVideos(prev => {
      if (prev.size === allVideoIds.length) {
        return new Set(); // 전체 해제
      } else {
        return new Set(allVideoIds); // 전체 선택
      }
    });
  };

        // 저장 버튼 핸들러 (post 테이블에 저장)
  const handleSave = async () => {
    if (!result || !result.result || !result.result.items) {
      setError('저장할 데이터가 없습니다. 먼저 조회를 실행해주세요.');
      return;
    }

    // 선택된 비디오가 없으면 전체 저장
    const videosToSave = selectedVideos.size > 0 
      ? result.result.items.filter(item => {
          const videoId = item.id?.videoId || (typeof item.id === 'string' ? item.id : null);
          return videoId && selectedVideos.has(videoId);
        })
      : result.result.items;

    if (videosToSave.length === 0) {
      setError('저장할 동영상이 없습니다. 동영상을 선택해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // WriteYoutubePostPage.jsx와 동일한 blocks_json 패턴으로 변환
      const processedVideos = videosToSave.map(video => {
        const videoId = video.id?.videoId || (typeof video.id === 'string' ? video.id : null);
        const snippet = video.snippet || {};
        const statistics = video.statistics || {};
        
        // YouTube URL 생성
        const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
        
        // 임베드 URL 생성
        const embedUrl = `https://www.youtube.com/embed/${videoId}`;
        
        // 조회수 포맷팅
        const formatViewCount = (count) => {
          if (!count) return '0';
          const num = parseInt(count);
          if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
          } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
          }
          return num.toString();
        };

        // 날짜 포맷팅
        const formatDate = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        };

        // content에 업데이트 날짜, 조회수, 좋아요 수 포함
        const contentWithStats = `📅 업데이트: ${formatDate(new Date().toISOString())}\n👁️ 조회수: ${formatViewCount(statistics.viewCount)}\n👍 좋아요: ${formatViewCount(statistics.likeCount)}\n\n${snippet.description?.substring(0, 800) || 'YouTube 인기 동영상'}`;

        // WriteYoutubePostPage.jsx와 동일한 blocks_json 구조
        const blocksJson = [
          {
            id: Date.now() + Math.random(),
            type: 'text',
            content: contentWithStats,
            placeholder: '내용을 입력하세요'
          },
          {
            id: Date.now() + Math.random() + 1,
            type: 'video',
            url: youtubeUrl,
            embedUrl: embedUrl,
            caption: `조회수: ${formatViewCount(statistics.viewCount)} | 좋아요: ${formatViewCount(statistics.likeCount)}`
          }
        ];

        return {
          ...video,
          title: snippet.title,
          content: contentWithStats,
          blocks_json: JSON.stringify(blocksJson),
          youtubeUrl: youtubeUrl,
          embedUrl: embedUrl,
          viewCount: statistics.viewCount,
          likeCount: statistics.likeCount,
          publishedAt: snippet.publishedAt
        };
      });

      const saveParams = new URLSearchParams();
      saveParams.append('endpoint', 'videos');
      
      // 원본 파라미터들 추가
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          saveParams.append(key, value.trim());
        }
      });

      // 저장 요청
      const response = await fetch(`${getApiUrl('/api/youtube/save-videos')}?${saveParams.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videos: processedVideos,
          params: result.params
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`성공적으로 ${data.savedCount}개의 동영상을 저장했습니다.`);
        setSelectedVideos(new Set()); // 선택 초기화
      } else {
        setError(data.message || '저장 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 텍스트 복사 함수
  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(prev => ({ ...prev, [type]: '복사됨' }));
      
      // 2초 후 상태 초기화
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [type]: '' }));
      }, 2000);
    } catch (err) {
      console.error('복사 실패:', err);
      setCopyStatus(prev => ({ ...prev, [type]: '복사 실패' }));
      
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [type]: '' }));
      }, 2000);
    }
  };

  // 제약사항 체크 함수
  const checkConstraints = (name, value) => {
    const newFormData = { ...formData, [name]: value };
    
    // videos 엔드포인트 필터 검사
    if ('videos' === 'videos') {
      const hasRequiredFilter = newFormData.id || newFormData.chart || newFormData.myRating;
      if (!hasRequiredFilter && (newFormData.videoCategoryId || newFormData.regionCode || newFormData.maxResults)) {
        const message = `⚠️ 제약사항 알림\n\nVideos API는 다음 중 하나의 필터가 필요합니다:\n- id (동영상 ID)\n- chart (인기 차트)\n- myRating (내 평가)\n\n해결 방법:\n1. Chart 파라미터를 추가하세요\n2. Search API를 사용하세요`;
        alert(message);
        setConstraintAlerts(prev => ({ ...prev, [name]: true }));
        return false;
      }
    }
    
    // chart=mostPopular와 videoCategoryId 호환성 검사
    if ('videos' === 'videos' && newFormData.chart === 'mostPopular' && newFormData.videoCategoryId) {
      // mostPopular 차트에서 실제로 지원되는 카테고리 ID들 (YouTube API 문서 기준)
      const supportedCategories = ['1', '2', '10', '15', '17', '20', '22', '23', '24', '25', '26', '27', '28', '29', '30', '32', '33'];
      
      // 여러 카테고리가 선택된 경우 쉼표로 분리하여 체크
      const selectedCategories = newFormData.videoCategoryId.split(',').map(cat => cat.trim());
      
      // 1. 지원되지 않는 카테고리 체크
      const unsupportedSelected = selectedCategories.filter(cat => !supportedCategories.includes(cat));
      
      if (unsupportedSelected.length > 0) {
        const categoryNames = {
          '19': '여행 및 이벤트',
          '31': '애니메이션',
          '34': '다큐멘터리',
          '35': '드라마', 
          '36': '가족',
          '37': '외국어',
          '38': '공포',
          '39': 'SF 및 판타지',
          '40': '스릴러',
          '41': '단편',
          '42': '쇼',
          '43': '예고편',
          '44': '기타'
        };
        
        const unsupportedNames = unsupportedSelected.map(cat => 
          categoryNames[cat] || `카테고리 ${cat}`
        ).join(', ');
        
        const message = `⚠️ 제약사항 알림\n\nchart=mostPopular에서는 다음 카테고리를 지원하지 않습니다:\n"${unsupportedNames}"\n\n지원되는 카테고리:\n• 1 (영화 및 애니메이션)\n• 2 (자동차)\n• 10 (음악)\n• 15 (애완동물 및 동물)\n• 17 (스포츠)\n• 20 (게임)\n• 22 (인물 및 블로그)\n• 23 (코미디)\n• 24 (엔터테인먼트)\n• 25 (뉴스 및 정치)\n• 26 (스타일 및 뷰티)\n• 27 (교육)\n• 28 (과학 및 기술)\n• 29 (비영리 및 활동)\n• 30 (영화)\n• 32 (액션/어드벤처)\n• 33 (클래식)\n\n해결 방법:\n1. 위의 지원되는 카테고리만 선택하세요\n2. 카테고리를 제거하고 모든 카테고리의 인기 동영상을 조회하세요`;
        
        alert(message);
        setConstraintAlerts(prev => ({ ...prev, [name]: true }));
        return false;
      }
      
      // 2. 여러 카테고리 선택 시 경고 (chart=mostPopular는 단일 카테고리만 지원)
      if (selectedCategories.length > 1) {
        const categoryNames = {
          '1': '영화 및 애니메이션',
          '2': '자동차',
          '10': '음악',
          '15': '애완동물 및 동물',
          '17': '스포츠',
          '20': '게임',
          '22': '인물 및 블로그',
          '23': '코미디',
          '24': '엔터테인먼트',
          '25': '뉴스 및 정치',
          '26': '스타일 및 뷰티',
          '27': '교육',
          '28': '과학 및 기술',
          '29': '비영리 및 활동',
          '30': '영화',
          '31': '애니메이션',
          '32': '액션/어드벤처',
          '33': '클래식'
        };
        
        const selectedNames = selectedCategories.map(cat => 
          categoryNames[cat] || `카테고리 ${cat}`
        ).join(', ');
        
        const message = `⚠️ 제약사항 알림\n\nchart=mostPopular에서는 여러 카테고리를 동시에 선택할 수 없습니다.\n\n현재 선택된 카테고리: "${selectedNames}"\n\n해결 방법:\n1. 하나의 카테고리만 선택하세요\n2. Chart 파라미터를 제거하고 Search API를 사용하세요`;
        
        alert(message);
        setConstraintAlerts(prev => ({ ...prev, [name]: true }));
        return false;
      }
    }

    // chart와 q 파라미터 동시 사용 제한
    if ('videos' === 'videos' && newFormData.chart && newFormData.q) {
      const message = `⚠️ 제약사항 알림\n\nchart 파라미터와 q(검색어) 파라미터는 동시에 사용할 수 없습니다.\n\n해결 방법:\n1. Chart 파라미터를 제거하고 검색어만 사용\n2. Search API를 사용하여 검색어로 검색`;
      alert(message);
      setConstraintAlerts(prev => ({ ...prev, [name]: true }));
      return false;
    }

    // videoId와 chart 동시 사용 제한
    if ('videos' === 'videos' && newFormData.id && newFormData.chart) {
      const message = `⚠️ 제약사항 알림\n\nvideoId와 chart 파라미터는 동시에 사용할 수 없습니다.\n\n해결 방법:\n1. Chart 파라미터를 제거하고 videoId만 사용\n2. 특정 동영상 정보만 조회하려면 videoId만 사용`;
      alert(message);
      setConstraintAlerts(prev => ({ ...prev, [name]: true }));
      return false;
    }

    setConstraintAlerts(prev => ({ ...prev, [name]: false }));
    return true;
  };

  const renderParameterField = (config) => {
    const { name, label, type, options, placeholder, required, description } = config;
    
    if (type === 'checkbox' && options) {
      return (
        <div key={name} className="form-group full-width">
          <label htmlFor={name} className={required ? 'required' : ''}>{label}:</label>
          {description && <div className="parameter-description">{description}</div>}
                     <div className="checkbox-group">
             {options.map(option => (
               <label key={option.value} className="checkbox-option">
                 <input
                   type="checkbox"
                   value={option.value}
                   checked={formData[name] ? formData[name].split(',').includes(option.value) : false}
                   onChange={(e) => {
                     const currentValues = formData[name] ? formData[name].split(',').filter(v => v.trim()) : [];
                     let newValue;
                     
                     if (e.target.checked) {
                       newValue = [...currentValues, option.value].join(',');
                     } else {
                       newValue = currentValues.filter(v => v !== option.value).join(',');
                     }
                     
                     // 제약사항 체크
                     if (checkConstraints(name, newValue)) {
                       setFormData(prev => ({
                         ...prev,
                         [name]: newValue
                       }));
                     }
                   }}
                 />
                   <span 
                    className={`checkbox-label ${option.supported ? 'supported-category' : 'unsupported-category'}`}
                  >
                    {option.label} 
                  </span>
               </label>
             ))}
           </div>
        </div>
      );
    }
    
    if (type === 'multiselect' && options) {
      const currentValues = formData[name] ? formData[name].split(',').filter(v => v.trim()) : [];
      const isOpen = multiselectOpen[name] || false;
      
      return (
        <div key={name} className="form-group">
          <label htmlFor={name} className={required ? 'required' : ''}>{label}:</label>
          {description && <div className="parameter-description">{description}</div>}
          <div className="multiselect-container">
            <div 
              className="multiselect-trigger"
              onClick={() => toggleMultiselect(name)}
            >
              <span className="multiselect-display">
                {currentValues.length > 0 
                  ? `${currentValues.length}개 선택됨` 
                  : '선택하세요'
                }
              </span>
              <span className="multiselect-arrow">▼</span>
            </div>
            {isOpen && (
              <div className="multiselect-dropdown">
                {/* 전체선택 체크박스 */}
                <label className="multiselect-option select-all-option">
                                     <input
                     type="checkbox"
                     checked={currentValues.length === options.length && options.length > 0}
                     onChange={() => handleParamSelectAll(name, options)}
                   />
                  <span className="multiselect-option-text">전체선택</span>
                </label>
                <div className="multiselect-divider"></div>
                {options.map(option => (
                  <label key={option.value} className="multiselect-option">
                    <input
                      type="checkbox"
                      checked={currentValues.includes(option.value)}
                      onChange={() => handleMultiselectChange(name, option.value)}
                    />
                    <span className="multiselect-option-text">{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    if (type === 'select' && options) {
      return (
        <div key={name} className="form-group">
          <label htmlFor={name} className={required ? 'required' : ''}>{label}:</label>
          {description && <div className="parameter-description">{description}</div>}
          <select
            id={name}
            name={name}
            value={formData[name] || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (checkConstraints(name, value)) {
                handleInputChange(e);
              }
            }}
            required={required}
          >
            <option value="">선택하세요</option>
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    
    return (
      <div key={name} className="form-group">
        <label htmlFor={name} className={required ? 'required' : ''}>{label}:</label>
        {description && <div className="parameter-description">{description}</div>}
        <input
          type="text"
          id={name}
          name={name}
          value={formData[name] || ''}
          onChange={(e) => {
            const value = e.target.value;
            if (checkConstraints(name, value)) {
              handleInputChange(e);
            }
          }}
          placeholder={placeholder}
          required={required}
        />
      </div>
    );
  };

  return (
    <div className="youtube-api-tester">
      <div className="page-header">
        <h1>YouTube Videos API 테스트</h1>
        <p>YouTube 동영상 정보를 조회하는 API입니다. 인기 동영상 차트, 특정 동영상 정보 등을 가져올 수 있습니다.</p>
      </div>

      <div className="form-section">
        <h3>API 파라미터 설정</h3>
        <form onSubmit={handleSubmit}>
          <div className="parameters-grid">
            {parameterConfigs.map(renderParameterField)}
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="search-button"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? '실행 중...' : '실행'}
            </button>
            <button 
              type="button" 
              className="submit-button"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'API 호출 중...' : 'API 호출'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="error-section">
          <h3>❌ 오류</h3>
          <div className="error-message">{error}</div>
        </div>
      )}

      {result && (
        <div className="result-section">
          <div className="result-header">
            <h3>✅ API 호출 결과</h3>
                         <button 
               type="button" 
               className="save-button"
               onClick={handleSave}
               disabled={loading}
             >
               {loading ? '저장 중...' : selectedVideos.size > 0 ? `📥 선택된 ${selectedVideos.size}개 저장` : '📥 전체 저장'}
             </button>
          </div>
          
          <div className="result-info">
            <div className="info-item">
              <strong>엔드포인트:</strong> {result.endpoint}
            </div>
            <div className="info-item">
              <strong>총 결과 수:</strong> {result.result.totalResults}
            </div>
            <div className="info-item">
              <strong>페이지당 결과 수:</strong> {result.result.resultsPerPage}
            </div>
            <div className="info-item">
              <strong>실제 반환된 항목:</strong> {result.result.items.length}개
            </div>
          </div>

                     {/* YouTube 미리보기 섹션 */}
           {result.result.items && result.result.items.length > 0 && (
             <div className="youtube-previews">
               <div className="preview-header">
                 <h4>🎬 YouTube 미리보기 ({result.result.items.length}개)</h4>
                 <div className="preview-actions">
                   <button 
                     type="button" 
                     className="select-all-button"
                     onClick={handleSelectAll}
                   >
                     {selectedVideos.size === result.result.items.length ? '전체 해제' : '전체 선택'}
                   </button>
                   <span className="selected-count">
                     {selectedVideos.size > 0 ? `${selectedVideos.size}개 선택됨` : ''}
                   </span>
                 </div>
               </div>
              <div className="video-grid">
                {result.result.items.map((item, index) => {
                  const videoId = item.id?.videoId || (typeof item.id === 'string' ? item.id : null);
                  const preview = videoId ? videoPreviews[videoId] : null;
                  
                  if (videoId && preview) {
                    // YouTube API에서 가져온 원본 데이터에서 추가 정보 추출
                    const snippet = item.snippet || {};
                    const statistics = item.statistics || {};
                    
                    // 조회수 포맷팅
                    const formatViewCount = (count) => {
                      if (!count) return '0';
                      const num = parseInt(count);
                      if (num >= 1000000) {
                        return (num / 1000000).toFixed(1) + 'M';
                      } else if (num >= 1000) {
                        return (num / 1000).toFixed(1) + 'K';
                      }
                      return num.toString();
                    };

                    // 날짜 포맷팅
                    const formatDate = (dateString) => {
                      if (!dateString) return '';
                      const date = new Date(dateString);
                      return date.toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    };

                                         return (
                       <div key={index} className="video-preview-item">
                         <div className="video-select">
                           <input
                             type="checkbox"
                             checked={selectedVideos.has(videoId)}
                             onChange={() => handleVideoSelect(videoId)}
                             title="이 동영상 선택"
                           />
                         </div>
                         <div className="video-thumbnail">
                           <img 
                             src={snippet.thumbnails?.high?.url || preview.thumbnail_url} 
                             alt={preview.title}
                             onClick={() => window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank')}
                             style={{ cursor: 'pointer' }}
                             title="클릭하여 YouTube에서 보기"
                           />
                         </div>
                                                 <div className="video-info">
                           <h5 
                             onClick={() => window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank')}
                             style={{ cursor: 'pointer', color: '#333' }}
                             title="클릭하여 YouTube에서 보기"
                           >
                             {preview.title}
                           </h5>
                          <p className="video-author">{preview.author_name}</p>
                          <div className="video-stats">
                            {snippet.publishedAt && (
                              <div className="video-stat">
                                <span>📅</span>
                                <span>{formatDate(snippet.publishedAt)}</span>
                              </div>
                            )}
                            {statistics.viewCount && (
                              <div className="video-stat">
                                <span>👁️</span>
                                <span>{formatViewCount(statistics.viewCount)} 조회</span>
                              </div>
                            )}
                            {statistics.likeCount && (
                              <div className="video-stat">
                                <span>👍</span>
                                <span>{formatViewCount(statistics.likeCount)} 좋아요</span>
                              </div>
                            )}
                            {statistics.commentCount && (
                              <div className="video-stat">
                                <span>💬</span>
                                <span>{formatViewCount(statistics.commentCount)} 댓글</span>
                              </div>
                            )}
                            {snippet.duration && (
                              <div className="video-stat">
                                <span>⏱️</span>
                                <span>{snippet.duration}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          <div className="parameters-display">
            <div className="display-header">
              <h4>사용된 파라미터:</h4>
              <button 
                type="button" 
                className="copy-button"
                onClick={() => handleCopy(JSON.stringify(result.params, null, 2), 'params')}
                title="파라미터 복사"
              >
                📋 {copyStatus.params || '복사'}
              </button>
            </div>
            <pre>{JSON.stringify(result.params, null, 2)}</pre>
          </div>

          <div className="results-display">
            <div className="display-header">
              <h4>결과 데이터:</h4>
              <button 
                type="button" 
                className="copy-button"
                onClick={() => handleCopy(JSON.stringify(result.result, null, 2), 'results')}
                title="결과 데이터 복사"
              >
                📋 {copyStatus.results || '복사'}
              </button>
            </div>
            <pre>{JSON.stringify(result.result, null, 2)}</pre>
          </div>
        </div>
      )}

      {/* 제약사항 안내 섹션 */}
      <div className="constraints-section">
        <h3>📋 YouTube API 제약사항</h3>
        <div className="constraints-content">
          <div className="constraint-group">
            <h4>🎬 Videos API 제약사항</h4>
            <ul>
              <li><strong>필수 필터:</strong> id, chart, myRating 중 하나는 반드시 필요합니다.</li>
              <li><strong>chart=mostPopular와 videoCategoryId 호환성:</strong> 일부 카테고리는 mostPopular 차트와 함께 사용할 수 없습니다.</li>
              <li><strong>chart와 q 파라미터:</strong> chart 파라미터와 검색어(q) 파라미터는 동시에 사용할 수 없습니다.</li>
              <li><strong>videoId와 chart:</strong> 특정 동영상 ID와 chart 파라미터는 동시에 사용할 수 없습니다.</li>
              <li><strong>⚠️ 주의:</strong> 카테고리 선택 시 API 오류가 발생하면 해당 카테고리를 제거하고 다시 시도해보세요.</li>
            </ul>
          </div>
          
          <div className="constraint-group">
            <h4>📋 일반 제약사항</h4>
            <ul>
              <li><strong>API 할당량:</strong> 일일 10,000 유닛 제한이 있습니다.</li>
              <li><strong>최대 결과 수:</strong> maxResults는 1-50 사이의 값이어야 합니다.</li>
              <li><strong>지역 코드:</strong> ISO 3166-1 alpha-2 형식(예: KR, US, JP)을 사용해야 합니다.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoutubeVideosApi;
